/**
 * 自由出行地点表（设计基准 docs/自由出行与小地图-设计方案.md §四）
 *
 * 结构：3 个上层地图节点（商业街/影视城/公园）+ 商业街内 6 家店（parent 指向节点）。
 * 用途：
 *  - `FixedEventDef.trigger.location` 的合法 ID 字典（特殊剧情事件挂点：各店可触发特殊剧情、公园挂特殊 NPC 初遇候选）；
 *  - 地图 UI 的地点卡文案来源（名称/主功能/社交说明）。
 * ⛔ 数值填料型地点：每个地点必须有它本来的作用（2026-10-09 茄子设计标准）。
 * 社交集中制：偶遇只发生在城市公园；商业街各店零偶遇；影视城=指定见人（不算偶遇）。
 */

export type LocationId =
    | 'loc_street'      // 梧桐商业街（节点）
    | 'loc_filmcity'    // 环亚影视城（节点）
    | 'loc_park'        // 城市公园（节点）
    | 'shop_hair'       // 露米娜发艺
    | 'shop_cloth'      // 缪斯服饰
    | 'shop_acc'        // 星辉珠宝
    | 'shop_derma'      // 蒂尔玛医疗美容
    | 'shop_gift'       // 心意礼品店
    | 'shop_cafe';      // 拾光咖啡馆

export interface LocationDef {
    id: LocationId;
    name: string;
    /** 商业街内店铺的父节点 */
    parent?: 'loc_street';
    /** 主功能描述（地点卡第 1 行） */
    desc: string;
    /** 社交说明（地点卡第 2 行；无社交演出的功能地点不填） */
    social?: string;
}

export const LOCATIONS: LocationDef[] = [
    // ===== 上层节点 =====
    {
        id: 'loc_street',
        name: '梧桐商业街',
        desc: '购物换装一条街，六家小店各有营生。',
        social: '零偶遇；各店可触发特殊剧情。',
    },
    {
        id: 'loc_filmcity',
        name: '环亚影视城',
        desc: '探班定向见面：指定见一位正在拍戏的已结识人脉。',
        social: '半定向必见；好感不算熟遇。',
    },
    {
        id: 'loc_park',
        name: '城市公园',
        desc: '散步，晒晒太阳。',
        social: '全游戏唯一偶遇点：必遇 1 人，遇到谁随机。',
    },
    // ===== 梧桐商业街 · 六家店 =====
    {
        id: 'shop_hair',
        name: '露米娜发艺',
        parent: 'loc_street',
        desc: '发型三槽（前/侧/后）+ 发色；买断拥有，买过的随时回店免费换。',
    },
    {
        id: 'shop_cloth',
        name: '缪斯服饰',
        parent: 'loc_street',
        desc: '服装；买断拥有，买完即上身。',
    },
    {
        id: 'shop_acc',
        name: '星辉珠宝',
        parent: 'loc_street',
        desc: '配饰 + 眼镜；买断拥有，买完即上身。',
    },
    {
        id: 'shop_derma',
        name: '蒂尔玛医疗美容',
        parent: 'loc_street',
        desc: '五官（脸型/眉/眼/鼻/嘴）+ 肤色 + 美瞳——全游戏唯一五官窗口；无门纯高价。',
    },
    {
        id: 'shop_gift',
        name: '心意礼品店',
        parent: 'loc_street',
        desc: '送人礼物，进背包；消耗品。',
    },
    {
        id: 'shop_cafe',
        name: '拾光咖啡馆',
        parent: 'loc_street',
        desc: '周末有约时来这里赴约；未来剧情舞台。',
        social: '零偶遇，全定向。',
    },
];

export const LOCATION_MAP: Record<string, LocationDef> = {};
LOCATIONS.forEach(l => { LOCATION_MAP[l.id] = l; });
